import React from "react";
import BackgroundRings from "../components/BackgroundRings";
import FadeIn from "../components/FadeIn";
import { waUrl } from "../lib/constants";

const PILLARS = [
    {
        n: "01",
        t: "Vibroacoustic Resonance",
        d: "Calibrated frequencies delivered through the body, not just the ears. Direct mechanical input to tissue.",
    },
    {
        n: "02",
        t: "Harmonic Locking",
        d: "Specific frequency ratios that force the brain to entrain. Not relaxation. Realignment.",
    },
    {
        n: "03",
        t: "Frequency-Targeted Intervention",
        d: "Low frequencies for sleep. Mid-range for anxiety. Complex harmonics for trauma patterns. Precision, not guesswork.",
    },
    {
        n: "04",
        t: "Autonomic Modulation",
        d: "The body self-regulates when given the right signal. No effort required. No breathing technique to master.",
    },
    {
        n: "05",
        t: "Neural Entrainment",
        d: "Brainwave synchronization through rhythmic stimulation. Delta and theta states — deep recovery previously accessible only through advanced meditation.",
    },
];

const Science = () => {
    return (
        <main data-testid="science-page">
            <section
                style={{
                    position: "relative",
                    minHeight: "80vh",
                    display: "flex",
                    alignItems: "center",
                    padding: "160px 32px 80px",
                    overflow: "hidden",
                }}
                data-testid="science-hero"
            >
                <BackgroundRings opacity={0.08} position="center" />
                <div
                    style={{
                        maxWidth: 1200,
                        margin: "0 auto",
                        position: "relative",
                        zIndex: 2,
                    }}
                >
                    <FadeIn>
                        <p className="eyebrow" style={{ marginBottom: 24 }}>
                            The Science
                        </p>
                        <h1
                            className="font-serif"
                            data-testid="science-headline"
                            style={{
                                color: "#ffffff",
                                fontSize: "clamp(44px, 7vw, 96px)",
                                fontWeight: 400,
                                lineHeight: 1.02,
                                letterSpacing: "-0.02em",
                                marginBottom: 32,
                                maxWidth: 1100,
                            }}
                        >
                            The science behind{" "}
                            <span style={{ color: "#c9a227" }}>
                                recovery.
                            </span>
                        </h1>
                        <p
                            style={{
                                color: "#c9a227",
                                fontSize: 20,
                                letterSpacing: "0.15em",
                                textTransform: "uppercase",
                                fontWeight: 300,
                            }}
                            data-testid="science-subhead"
                        >
                            Physics, not belief.
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* What happens in 20 minutes - intro */}
            <section
                style={{
                    padding: "80px 32px 0",
                    borderTop: "1px solid rgba(201,162,39,0.1)",
                }}
                data-testid="science-intro"
            >
                <div style={{ maxWidth: 1100, margin: "0 auto" }}>
                    <FadeIn>
                        <h2
                            data-testid="science-intro-heading"
                            style={{
                                color: "#c9a227",
                                fontFamily:
                                    "'Playfair Display', Georgia, serif",
                                fontSize: "clamp(28px, 3.6vw, 40px)",
                                fontWeight: 400,
                                lineHeight: 1.15,
                                letterSpacing: "-0.01em",
                                marginBottom: 32,
                            }}
                        >
                            What happens in 20 minutes.
                        </h2>
                        <div
                            style={{
                                color: "#ffffff",
                                fontSize: 18,
                                lineHeight: 1.8,
                                maxWidth: 820,
                                fontWeight: 300,
                            }}
                        >
                            <p style={{ marginTop: 0 }}>
                                You lie down fully clothed on a
                                precision-calibrated surface. Below you:
                                layered frequency fields that interact with
                                the body's own electrical and mechanical
                                systems. Around you: sound delivered through
                                multiple pathways — direct tissue contact,
                                spatial acoustic field, and targeted resonance
                                points — each tuned to a specific therapeutic
                                bandwidth.
                            </p>
                            <p>
                                The practitioner selects the frequency profile
                                based on what brought you here. Sleep requires
                                a different signature than anxiety. Athletic
                                recovery different than trauma patterns. Every
                                session is composed in real time, not played
                                from a playlist.
                            </p>
                            <p style={{ color: "#888888" }}>
                                Demos are 20 minutes. Full sessions run 60 to
                                90 minutes, depending on what the client
                                needs.
                            </p>
                            <p style={{ marginBottom: 0 }}>
                                No crystals. No incense. No belief required.
                                Just physics, applied with precision.
                            </p>
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* Pillars */}
            <section
                style={{
                    padding: "80px 32px 120px",
                }}
                data-testid="science-pillars"
            >
                <div style={{ maxWidth: 1100, margin: "0 auto" }}>
                    {PILLARS.map((p, i) => (
                        <FadeIn key={p.n} delay={i * 80}>
                            <article
                                data-testid={`science-pillar-${i + 1}`}
                                style={{
                                    display: "grid",
                                    gridTemplateColumns:
                                        "minmax(120px, 180px) 1fr",
                                    gap: 48,
                                    padding: "56px 0",
                                    borderTop:
                                        i === 0
                                            ? "1px solid rgba(201,162,39,0.25)"
                                            : "1px solid rgba(201,162,39,0.1)",
                                }}
                            >
                                <span
                                    className="font-serif"
                                    style={{
                                        color: "#c9a227",
                                        fontSize: 56,
                                        fontWeight: 400,
                                        lineHeight: 1,
                                    }}
                                >
                                    {p.n}
                                </span>
                                <div>
                                    <h2
                                        className="font-serif"
                                        style={{
                                            color: "#ffffff",
                                            fontSize:
                                                "clamp(28px, 3.5vw, 44px)",
                                            fontWeight: 400,
                                            lineHeight: 1.15,
                                            letterSpacing: "-0.01em",
                                            marginBottom: 20,
                                        }}
                                    >
                                        {p.t}
                                    </h2>
                                    <p
                                        style={{
                                            color: "#a8a8a8",
                                            fontSize: 18,
                                            lineHeight: 1.75,
                                            maxWidth: 760,
                                            margin: 0,
                                            fontWeight: 300,
                                        }}
                                    >
                                        {p.d}
                                    </p>
                                </div>
                            </article>
                        </FadeIn>
                    ))}
                </div>
            </section>

            <section
                style={{
                    padding: "100px 32px",
                    borderTop: "1px solid rgba(201,162,39,0.15)",
                    backgroundColor: "#070707",
                    textAlign: "center",
                }}
            >
                <FadeIn>
                    <h2
                        className="font-serif"
                        style={{
                            color: "#ffffff",
                            fontSize: "clamp(28px, 4vw, 48px)",
                            fontWeight: 400,
                            lineHeight: 1.1,
                            marginBottom: 32,
                            maxWidth: 800,
                            margin: "0 auto 32px",
                        }}
                    >
                        Feel the science. Reserve a demo.
                    </h2>
                    <a
                        href={waUrl(
                            "Hi, I'd like to reserve a session (Science page).",
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-gold-solid"
                        data-testid="science-cta"
                    >
                        Reserve Your Session
                    </a>
                    <p
                        style={{
                            color: "#c9a227",
                            fontSize: 13,
                            margin: "20px auto 0",
                            lineHeight: 1.6,
                            fontWeight: 400,
                            letterSpacing: "0.02em",
                        }}
                        data-testid="science-cta-tagline"
                    >
                        Feel the shift in your energy. Then pay.
                    </p>
                    <p
                        style={{
                            color: "#888",
                            fontSize: 14,
                            margin: "12px auto 0",
                            lineHeight: 1.65,
                            maxWidth: 560,
                        }}
                        data-testid="science-cta-subtext"
                    >
                        20–30 minutes. No commitment. Same equipment. Same
                        practitioner.
                    </p>
                </FadeIn>
            </section>
        </main>
    );
};

export default Science;
